import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Loader2, AlertCircle } from "lucide-react";
import "./BreakfastSurvey.css";

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  title: "Choix du petit-déjeuner",
  intro: "",
  times: ["9H00", "9H30", "10H00", "10H30"],
  egg_choices: ["Bacon", "Fromage", "Nature"],
  drinks: ["Café", "Thé"],
  butters: ["Au thym", "Nature"],
  jams: ["Ananas passion", "Mangue passion", "Tomate"],
  jam_count: 2,
  labels: {
    name: "Votre nom", date: "Date souhaitée", time: "Heure souhaitée",
    eggs: "Œufs brouillés avec", drink: "Boisson chaude",
    butter: "Beurre maison à partager", jams: "Deux confitures à partager",
    comments: "Allergies ou remarques",
  },
  required: { name: false, time: true, eggs: true, drink: true, butter: true, jams: true },
};

const DAYS_LONG = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const MONTHS_LONG = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  return `${DAYS_LONG[d.getDay()]} ${d.getDate()} ${MONTHS_LONG[d.getMonth()]}`;
}

function Choice({ options, value, onPick, testid, invalid }) {
  return (
    <div className={`bk-opts ${invalid ? "bk-invalid" : ""}`} data-testid={testid}>
      {options.map((o) => (
        <button type="button" key={o} className={`bk-opt ${value === o ? "sel" : ""}`}
          onClick={() => onPick(value === o ? null : o)} data-testid={`${testid}-${o}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

export default function BreakfastSurvey() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [cfg, setCfg] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const [name, setName] = useState("");
  const [dateIso, setDateIso] = useState("");
  const [time, setTime] = useState(null);
  const [voy, setVoy] = useState([{ protein: null, boisson: null }, { protein: null, boisson: null }, { protein: null, boisson: null }]);
  const [beurre, setBeurre] = useState(null);
  const [confitures, setConfitures] = useState([]);
  const [comments, setComments] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/rg/public/breakfast/${token}`);
        if (!r.ok) { setNotFound(true); return; }
        const data = await r.json();
        setCtx(data);
        if (data.config) setCfg({ ...DEFAULTS, ...data.config, labels: { ...DEFAULTS.labels, ...(data.config.labels || {}) }, required: { ...DEFAULTS.required, ...(data.config.required || {}) } });
        if (data.first_name) setName(data.first_name);
        if ((data.allowed_dates || []).length === 1) setDateIso(data.allowed_dates[0]);
        // Pré-remplir si une réponse a déjà été reçue (modification possible)
        const ex = data.existing;
        if (ex) {
          if (ex.name) setName(ex.name);
          if (ex.date) setDateIso(ex.date);
          if (ex.time) {
            const t = (data.config?.times || DEFAULTS.times).find((x) => x.replace(/[^\d]/g, "") === String(ex.time).replace(/[^\d]/g, ""));
            if (t) setTime(t);
          }
          if (Array.isArray(ex.voyageurs) && ex.voyageurs.length) {
            setVoy((p) => p.map((v, i) => (ex.voyageurs[i] ? { protein: ex.voyageurs[i].protein || null, boisson: ex.voyageurs[i].boisson || null } : v)));
          }
          if (ex.beurre) setBeurre(ex.beurre);
          if (Array.isArray(ex.confitures)) setConfitures(ex.confitures);
          if (ex.comments) setComments(ex.comments);
        }
      } catch { setNotFound(true); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const people = ctx?.people || 2;
  const requiredCount = Math.min(people, 2);
  const jamCount = cfg.jam_count || 2;
  const clearErr = (k) => setErrors((e) => { if (!e[k]) return e; const n = { ...e }; delete n[k]; return n; });
  const setVoyField = (i, field, val) => { setVoy((p) => p.map((v, idx) => (idx === i ? { ...v, [field]: val } : v))); clearErr(`v${i}-${field}`); };
  const toggleConf = (c) => { setConfitures((p) => (p.includes(c) ? p.filter((x) => x !== c) : p.length >= jamCount ? p : [...p, c])); clearErr("confitures"); };

  const validate = () => {
    const e = {};
    if (!dateIso) e.date = `Choisissez une date`;
    if (cfg.required.time && !time) e.time = `Choisissez une heure`;
    for (let i = 0; i < requiredCount; i++) {
      if (cfg.required.eggs && !voy[i].protein) e[`v${i}-protein`] = `Voyageur ${i + 1} : choisissez les œufs`;
      if (cfg.required.drink && !voy[i].boisson) e[`v${i}-boisson`] = `Voyageur ${i + 1} : choisissez une boisson`;
    }
    if (cfg.required.butter && !beurre) e.beurre = `Choisissez un beurre`;
    if (cfg.required.jams && confitures.length !== jamCount) {
      e.confitures = confitures.length < jamCount
        ? `Choisissez encore ${jamCount - confitures.length} confiture${jamCount - confitures.length > 1 ? "s" : ""}`
        : `Choisissez seulement ${jamCount} confitures`;
    }
    return e;
  };

  const submit = async () => {
    if (submitting) return;
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      const order = ["date", "time", "v0-protein", "v0-boisson", "v1-protein", "v1-boisson", "beurre", "confitures"];
      const first = order.find((k) => e[k]);
      if (first) {
        const el = document.getElementById(`bk-field-${first}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    setSubmitting(true);
    setServerError("");
    const voyageurs = voy.slice(0, people).filter((v) => v.protein || v.boisson);
    try {
      const r = await fetch(`${API}/api/rg/public/breakfast/${token}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, date: dateIso, time, voyageurs, beurre, confitures, comments }),
      });
      if (!r.ok) {
        let msg = "Une erreur est survenue, merci de réessayer.";
        try { msg = (await r.json()).detail || msg; } catch { /* keep */ }
        setServerError(msg); return;
      }
      setDone(true);
    } catch { setServerError("Connexion impossible. Vérifiez votre réseau et réessayez."); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="bk-wrap"><div className="bk-card bk-center" data-testid="bk-loading">
      <Loader2 size={28} className="bk-spin" /><p className="bk-sub" style={{ marginTop: 12 }}>Chargement…</p>
    </div></div>
  );
  if (notFound) return (
    <div className="bk-wrap"><div className="bk-card bk-center" data-testid="bk-notfound">
      <p className="bk-title">Lien invalide</p>
      <p className="bk-sub">Ce lien n&apos;est plus valide. Merci de contacter votre hôte.</p>
    </div></div>
  );
  if (done) return (
    <div className="bk-wrap"><div className="bk-card bk-center" data-testid="bk-done">
      <span className="bk-done-icon"><Check size={30} /></span>
      <p className="bk-title">Merci {name} ✨</p>
      <p className="bk-sub">Vos choix de petit-déjeuner ont bien été enregistrés.<br />Nous préparons tout avec soin pour votre séjour 🥐</p>
    </div></div>
  );

  const L = cfg.labels;
  const errList = Object.values(errors);

  return (
    <div className="bk-wrap">
      <div className="bk-card" data-testid="bk-form">
        <p className="bk-eyebrow">Curieux Refuge</p>
        <h1 className="bk-title">{cfg.title}</h1>
        {cfg.intro ? <p className="bk-sub" style={{ whiteSpace: "pre-line" }}>{cfg.intro}</p> : <div style={{ height: 8 }} />}

        {ctx.already_received && (
          <div className="bk-notice" data-testid="bk-already-received">
            Vous avez déjà répondu — vos choix sont pré-remplis ci-dessous. Vous pouvez les modifier puis revalider.
          </div>
        )}

        {serverError && <div className="bk-error" data-testid="bk-error">{serverError}</div>}

        <div className="bk-q" id="bk-field-name">
          <label className="bk-q-label">{L.name}</label>
          <input className="bk-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="bk-name" placeholder="Prénom" />
        </div>

        <div className="bk-q" id="bk-field-date">
          <label className="bk-q-label">{L.date}<span className="bk-req">*</span></label>
          {(ctx.allowed_dates || []).length > 0 ? (
            <div className={`bk-opts ${errors.date ? "bk-invalid" : ""}`} data-testid="bk-date-opts">
              {ctx.allowed_dates.map((iso) => (
                <button type="button" key={iso} className={`bk-opt ${dateIso === iso ? "sel" : ""}`}
                  onClick={() => { setDateIso(iso); clearErr("date"); }} data-testid={`bk-date-${iso}`}>
                  {fmtDate(iso)}
                </button>
              ))}
            </div>
          ) : (
            <input type="date" className="bk-date" value={dateIso} onChange={(e) => { setDateIso(e.target.value); clearErr("date"); }} data-testid="bk-date-input" />
          )}
          {errors.date && <p className="bk-field-err" data-testid="bk-err-date">{errors.date}</p>}
        </div>

        <div className="bk-q" id="bk-field-time">
          <label className="bk-q-label">{L.time}{cfg.required.time && <span className="bk-req">*</span>}</label>
          <Choice options={cfg.times} value={time} onPick={(v) => { setTime(v); clearErr("time"); }} testid="bk-time" invalid={!!errors.time} />
          {errors.time && <p className="bk-field-err" data-testid="bk-err-time">{errors.time}</p>}
        </div>

        {Array.from({ length: people }).map((_, i) => {
          const required = i < requiredCount;
          return (
            <div className="bk-voy" key={i} data-testid={`bk-voy-${i}`}>
              <p className="bk-voy-title">Voyageur {i + 1}{required ? "" : " (optionnel)"}</p>
              <div className="bk-q" id={`bk-field-v${i}-protein`}>
                <label className="bk-q-label">{L.eggs}{required && cfg.required.eggs ? <span className="bk-req">*</span> : null}</label>
                <Choice options={cfg.egg_choices} value={voy[i].protein} onPick={(v) => setVoyField(i, "protein", v)} testid={`bk-v${i}-protein`} invalid={!!errors[`v${i}-protein`]} />
                {errors[`v${i}-protein`] && <p className="bk-field-err">{errors[`v${i}-protein`]}</p>}
              </div>
              <div className="bk-q" id={`bk-field-v${i}-boisson`}>
                <label className="bk-q-label">{L.drink}{required && cfg.required.drink ? <span className="bk-req">*</span> : null}</label>
                <Choice options={cfg.drinks} value={voy[i].boisson} onPick={(v) => setVoyField(i, "boisson", v)} testid={`bk-v${i}-boisson`} invalid={!!errors[`v${i}-boisson`]} />
                {errors[`v${i}-boisson`] && <p className="bk-field-err">{errors[`v${i}-boisson`]}</p>}
              </div>
            </div>
          );
        })}

        <div className="bk-q" id="bk-field-beurre">
          <label className="bk-q-label">{L.butter}{cfg.required.butter && <span className="bk-req">*</span>}</label>
          <Choice options={cfg.butters} value={beurre} onPick={(v) => { setBeurre(v); clearErr("beurre"); }} testid="bk-beurre" invalid={!!errors.beurre} />
          {errors.beurre && <p className="bk-field-err" data-testid="bk-err-beurre">{errors.beurre}</p>}
        </div>

        <div className="bk-q" id="bk-field-confitures">
          <label className="bk-q-label">{L.jams}{cfg.required.jams && <span className="bk-req">*</span>}</label>
          <span className="bk-q-hint">Choisissez exactement {jamCount} confiture{jamCount > 1 ? "s" : ""} ({confitures.length}/{jamCount}).</span>
          <div className={`bk-opts ${errors.confitures ? "bk-invalid" : ""}`} data-testid="bk-confitures">
            {cfg.jams.map((c) => {
              const sel = confitures.includes(c);
              const disabled = !sel && confitures.length >= jamCount;
              return (
                <button type="button" key={c} className={`bk-opt ${sel ? "sel" : ""}`}
                  style={disabled ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
                  onClick={() => toggleConf(c)} data-testid={`bk-conf-${c}`}>
                  {c}
                </button>
              );
            })}
          </div>
          {errors.confitures && <p className="bk-field-err" data-testid="bk-err-confitures">{errors.confitures}</p>}
        </div>

        <div className="bk-q">
          <label className="bk-q-label">{L.comments}</label>
          <textarea className="bk-textarea" value={comments} onChange={(e) => setComments(e.target.value)} data-testid="bk-comments" placeholder="Facultatif" />
        </div>

        {errList.length > 0 && (
          <div className="bk-error" data-testid="bk-error-recap">
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, marginBottom: 4 }}>
              <AlertCircle size={14} /> Il manque {errList.length} information{errList.length > 1 ? "s" : ""} :
            </div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {errList.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        <button type="button" className="bk-submit" disabled={submitting} onClick={submit} data-testid="bk-submit">
          {submitting ? <><Loader2 size={18} className="bk-spin" /> Envoi…</> : <>Valider mes choix</>}
        </button>
      </div>
    </div>
  );
}
